import React from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import {
    AdjustmentsHorizontalIcon,
    ClockIcon,
    CpuChipIcon,
    InformationCircleIcon,
    PlusIcon,
    Squares2X2Icon,
} from '@heroicons/react/24/solid';
import { LoadingSpinner } from '@/components';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { ClientLogo } from '@/components/ClientLogo';
import type { ClientLogoVariant } from '@/components/ClientLogo';
import { formatCapacityLabel } from '@/features/Collaborators/Allocations/utils/formatCapacityLabel';
import { VacationBadge } from './VacationBadge';
import type { GroupedEvents } from './CollaboratorAllocationsOverviewPage';
import type { AllocationTimelineEventSeverity } from '@/features/Collaborators/Models';

type Severity = AllocationTimelineEventSeverity;

interface CollaboratorLookup {
    name: string;
    photoUrl?: string | null;
    primaryCapability?: string | null;
    vacationSummary?: any; // Passed from useCollaboratorsMap
}

interface TimelineViewProps {
    groups: GroupedEvents[];
    isInitialLoading: boolean;
    hasEvents: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    isLoadingMore: boolean;
    collaboratorsMap: Map<string, CollaboratorLookup>;
    severityMetaMap: Record<Severity, { label: string; color: string; soft: string }>;
}


const formatDate = (value: Date | null | undefined, options: Intl.DateTimeFormatOptions): string | null => {
    if (!value) return null;
    try {
        return value.toLocaleString('pt-BR', options);
    } catch {
        return value.toISOString();
    }
};

const TIMELINE_LOGO_VARIANT: ClientLogoVariant = 32;
interface ClientBadgeProps {
    clientId?: string | null;
    name: string;
    projectName?: string | null;
    variant?: ClientLogoVariant;
}

const ClientBadge: React.FC<ClientBadgeProps> = ({ clientId, name, projectName, variant = TIMELINE_LOGO_VARIANT }) => {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm">
            <ClientLogo
                client={{ id: clientId, name, logoUrl: null }}
                clientId={clientId ?? undefined}
                variant={variant}
                className="h-6 w-6 rounded-full ring-0"
            />
            <span className="flex flex-col leading-tight text-left">
                <span>{name}</span>
                {projectName && <span className="text-[10px] font-normal text-gray-400">{projectName}</span>}
            </span>
        </span>
    );
};

const TimelineView: React.FC<TimelineViewProps> = ({
    groups,
    isInitialLoading,
    hasEvents,
    hasMore,
    onLoadMore,
    isLoadingMore,
    collaboratorsMap,
    severityMetaMap,
}) => {
    if (isInitialLoading) {
        return (
            <section className="rounded-lg">
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            </section>
        );
    }

    if (!hasEvents) {
        return (
            <section className="rounded-lg">
                <div className="px-6 py-16 text-center text-sm text-gray-500">Nenhum evento encontrado com os filtros atuais.</div>
            </section>
        );
    }

    return (
        <section className="rounded-lg">
            <div className="relative">
                <div className="absolute left-5 top-0 bottom-0 hidden border-l border-gray-200 md:block" aria-hidden="true" />
                <div className="space-y-10 py-6">
                    {groups.map((group) => (
                        <div key={group.dateKey} className="relative">
                            <div className="flex items-baseline gap-3">
                                <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">
                                    <ClockIcon className="h-4 w-4" />
                                    <span>{group.label}</span>
                                </div>
                                <span className="text-xs text-gray-400">{group.fullLabel}</span>
                            </div>

                            <ol className="mt-6 space-y-6 md:pl-10">
                                {group.events.map(({ event, createdAt, message, actorName, collaboratorName, severity, meta, vacationLabel }) => {
                                    const severityMeta = severityMetaMap[severity] ?? severityMetaMap.info;
                                    const Icon = meta.icon ?? InformationCircleIcon;
                                    const createdAtLabel = formatDate(createdAt, {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                        day: '2-digit',
                                        month: 'short',
                                    });

                                    const allocationLink = event.collaboratorId && event.allocationId
                                        ? `/collaborators/${event.collaboratorId}/allocations/${event.allocationId}/edit`
                                        : null;
                                    const collaboratorAllocationsLink = event.collaboratorId
                                        ? `/collaborators/${event.collaboratorId}/allocations`
                                        : null;
                                    const cardLink = allocationLink ?? collaboratorAllocationsLink;

                                    const collaboratorFallback = collaboratorsMap.get(event.collaboratorId ?? '');
                                    const isAutomatedEvent = actorName === 'Sistema';
                                    const actorAvatar = isAutomatedEvent ? null : event.actor?.avatar ?? null;
                                    const collaboratorAvatar = event.collaborator?.avatar ?? collaboratorFallback?.photoUrl ?? null;
                                    const actorDisplayName = isAutomatedEvent ? 'Automático' : actorName;
                                    const capacityChipLabel = formatCapacityLabel({
                                        capacityLabel: message.capacityLabel,
                                        capacityTypeName: message.capacityTypeName,
                                        capacityTechnologyName: message.capacityTechnologyName,
                                        capacitySeniorityName: message.capacitySeniorityName,
                                    });

                                    return (
                                        <li
                                            key={event.id}
                                            className="relative"
                                        >
                                            <div className="relative md:pl-12">
                                                <span className={clsx('absolute left-5 -ml-1 hidden h-full w-0.5 bg-gray-200 md:block', 'top-6')} aria-hidden="true" />
                                                <span className={clsx('absolute left-3 hidden h-2.5 w-2.5 rounded-full border-2 border-white md:block', severityMeta.color)} />
                                                <article className="relative rounded-lg border border-gray-200 bg-white transition hover:border-gray-300 hover:bg-gray-100">
                                                    {cardLink && (
                                                        <Link
                                                            to={cardLink}
                                                            className="absolute inset-0 z-10 rounded-lg"
                                                            aria-label={`Abrir alocação de ${collaboratorName}`}
                                                        />
                                                    )}
                                                    <div className="flex flex-col gap-4 p-4 sm:p-5">
                                                        <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                                                            <div className="flex flex-col gap-3">
                                                                <p className="text-sm font-semibold text-gray-900">{message.title}</p>
                                                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                                                    <CollaboratorAvatar photoUrl={collaboratorAvatar ?? undefined} name={collaboratorName} size="xsmall" />
                                                                    <span className="font-medium text-gray-900">{collaboratorName}</span>
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                                    {message.clientName && (
                                                                        <ClientBadge
                                                                            clientId={message.clientId}
                                                                            name={message.clientName}
                                                                            projectName={message.projectName ?? null}
                                                                            variant={TIMELINE_LOGO_VARIANT}
                                                                        />
                                                                    )}
                                                                    {capacityChipLabel && (
                                                                        <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-[11px] font-semibold text-primary-600">
                                                                            <Squares2X2Icon className="h-3.5 w-3.5" />
                                                                            Papel: {capacityChipLabel}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {message.details.length > 0 && (
                                                                    <ul className="flex flex-wrap gap-2 pt-1">
                                                                        {message.details.map((detail, index) => (
                                                                            <li key={index} className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-600">
                                                                                {detail}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                )}
                                                                <VacationBadge
                                                                    summary={collaboratorFallback?.vacationSummary}
                                                                    allocationId={event.allocationId}
                                                                />
                                                            </div>
                                                            <aside className="flex flex-col items-start gap-2 text-left lg:items-end lg:text-right">
                                                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                                                    <ClockIcon className="h-4 w-4" />
                                                                    {createdAtLabel ?? 'Horário não disponível'}
                                                                </div>
                                                                <div className="flex flex-wrap gap-2">
                                                                    <span className={clsx('inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium', meta.tone)}>
                                                                        <Icon className="h-4 w-4" />
                                                                        {meta.label}
                                                                    </span>
                                                                    <span className={clsx('inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium', severityMeta.soft)}>
                                                                        {severityMeta.label}
                                                                    </span>
                                                                </div>
                                                                <div className="flex justify-start lg:justify-end">
                                                                    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                                                        {isAutomatedEvent ? (
                                                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-slate-600">
                                                                                <CpuChipIcon className="h-4 w-4" aria-hidden="true" />
                                                                            </span>
                                                                        ) : (
                                                                            <CollaboratorAvatar
                                                                                photoUrl={actorAvatar ?? undefined}
                                                                                name={actorDisplayName}
                                                                                size="xsmall"
                                                                            />
                                                                        )}
                                                                        {actorDisplayName}
                                                                    </span>
                                                                </div>
                                                                <div className="flex flex-wrap justify-start gap-3 text-xs font-medium text-gray-500 lg:justify-end">
                                                                    {collaboratorAllocationsLink && (
                                                                        <Link
                                                                            to={collaboratorAllocationsLink}
                                                                            className="relative z-20 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 transition hover:border-indigo-400 hover:text-indigo-600"
                                                                        >
                                                                            <AdjustmentsHorizontalIcon className="h-4 w-4" />
                                                                            Alocações
                                                                        </Link>
                                                                    )}
                                                                </div>
                                                            </aside>
                                                        </header>
                                                    </div>
                                                </article>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    ))}
                </div>

                {hasMore && (
                    <div className="border-t border-gray-100 px-4 py-4 text-center sm:px-6">
                        <button
                            type="button"
                            onClick={onLoadMore}
                            disabled={isLoadingMore}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:border-indigo-400 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                            {isLoadingMore ? (
                                <>
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" aria-hidden="true" />
                                    Carregando...
                                </>
                            ) : (
                                <>
                                    <PlusIcon className="h-4 w-4" />
                                    Carregar mais
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default TimelineView;
